import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  Loader2,
  MoreVertical,
  Filter,
  GripVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO, isSameDay, startOfWeek, endOfWeek, addDays } from 'date-fns';

const CONTENT_TYPES = ['Blog', 'Email', 'Social', 'Podcast', 'Webinar', 'Landing Page', 'Press', 'Video', 'Event', 'Other'];
const CHANNELS = ['Website', 'LinkedIn', 'Twitter', 'Facebook', 'Instagram', 'YouTube', 'Email', 'Newsletter'];

// Calendar type color configuration (Phase 2)
const CALENDAR_TYPE_CONFIG = {
  event: { label: 'Event', color: '#8b5cf6', bgClass: 'bg-purple-100 border-purple-500' },
  email_campaign: { label: 'Email Campaign', color: '#3b82f6', bgClass: 'bg-blue-100 border-blue-500' },
  social_media: { label: 'Social Media Post', color: '#10b981', bgClass: 'bg-emerald-100 border-emerald-500' },
  press_release: { label: 'Press Release', color: '#f59e0b', bgClass: 'bg-amber-100 border-amber-500' },
  other: { label: 'Other', color: '#6b7280', bgClass: 'bg-slate-100 border-slate-500' }
};

const STATUS_CONFIG = {
  idea: { label: 'Idea', color: 'bg-slate-100 text-slate-700' },
  drafting: { label: 'Drafting', color: 'bg-yellow-50 text-yellow-700' },
  in_review: { label: 'In Review', color: 'bg-purple-50 text-purple-700' },
  approved: { label: 'Approved', color: 'bg-blue-50 text-blue-700' },
  scheduled: { label: 'Scheduled', color: 'bg-cyan-50 text-cyan-700' },
  published: { label: 'Published', color: 'bg-emerald-50 text-emerald-700' }
};

const CONTENT_TYPE_COLORS = {
  Blog: 'event-blog',
  Email: 'event-email',
  Social: 'event-social',
  Webinar: 'event-webinar',
  Podcast: 'event-podcast',
  Video: 'event-video',
  Press: 'event-press',
  'Landing Page': 'event-landing',
  Other: 'event-other'
};

const CalendarEvent = ({ content, onClick, onDragStart }) => {
  // Use calendar_type for color if available, otherwise fall back to content_type
  const calendarType = content.calendar_type || 'other';
  const typeConfig = CALENDAR_TYPE_CONFIG[calendarType] || CALENDAR_TYPE_CONFIG.other;
  const [dragging, setDragging] = useState(false);
  
  const handleDragStart = (e) => {
    setDragging(true);
    e.dataTransfer.setData('contentId', content.id);
    e.dataTransfer.setData('contentType', 'content');
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    // Visual feedback
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    setDragging(false);
    e.target.style.opacity = '1';
  };
  
  return (
    <div
      className={`p-1.5 rounded text-xs cursor-grab active:cursor-grabbing transition-all border-l-2 ${typeConfig.bgClass} ${
        dragging ? 'ring-2 ring-blue-500 scale-105 shadow-lg' : ''
      }`}
      style={{ borderLeftColor: typeConfig.color }}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onClick(content);
      }}
      data-testid={`calendar-content-${content.id}`}
    >
      <span className="font-medium truncate block text-slate-800">{content.title}</span>
      {content.live_link && (
        <span className="text-[10px] text-blue-600 truncate block">🔗 Live</span>
      )}
    </div>
  );
};

// Draggable Event (for events collection)
const DraggableEvent = ({ event, onClick }) => {
  const [dragging, setDragging] = useState(false);
  
  const handleDragStart = (e) => {
    setDragging(true);
    e.dataTransfer.setData('eventId', event.id);
    e.dataTransfer.setData('contentType', 'event');
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    setDragging(false);
    e.target.style.opacity = '1';
  };
  
  return (
    <div
      className={`calendar-event bg-emerald-50 border-l-emerald-500 cursor-grab active:cursor-grabbing transition-all ${
        dragging ? 'ring-2 ring-emerald-500 scale-105 shadow-lg' : ''
      }`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(event);
      }}
      data-testid={`calendar-event-${event.id}`}
    >
      <span className="font-medium truncate">{event.name}</span>
    </div>
  );
};

// Week View Content Card (draggable)
const WeekContentCard = ({ content, onClick }) => {
  const [dragging, setDragging] = useState(false);
  const colorClass = CONTENT_TYPE_COLORS[content.content_type] || 'event-other';
  
  const handleDragStart = (e) => {
    setDragging(true);
    e.dataTransfer.setData('contentId', content.id);
    e.dataTransfer.setData('contentType', 'content');
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    setDragging(false);
    e.target.style.opacity = '1';
  };
  
  return (
    <div
      className={`p-2 rounded-lg ${colorClass} cursor-grab active:cursor-grabbing transition-all ${
        dragging ? 'ring-2 ring-blue-500 scale-105 shadow-lg' : ''
      }`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onClick(content);
      }}
    >
      <p className="text-xs font-medium text-slate-900 line-clamp-2">{content.title}</p>
      <p className="text-[10px] text-slate-500 mt-1">{content.content_type}</p>
      {content.publish_time && (
        <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-1">
          <Clock className="w-3 h-3" />
          {content.publish_time}
        </p>
      )}
    </div>
  );
};

// Week View Event Card (draggable)
const WeekEventCard = ({ event }) => {
  const [dragging, setDragging] = useState(false);
  
  const handleDragStart = (e) => {
    setDragging(true);
    e.dataTransfer.setData('eventId', event.id);
    e.dataTransfer.setData('contentType', 'event');
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    setDragging(false);
    e.target.style.opacity = '1';
  };
  
  return (
    <div
      className={`p-2 rounded-lg bg-emerald-50 border-l-2 border-emerald-500 cursor-grab active:cursor-grabbing transition-all ${
        dragging ? 'ring-2 ring-emerald-500 scale-105 shadow-lg' : ''
      }`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <p className="text-xs font-medium text-slate-900 line-clamp-2">{event.name}</p>
      <p className="text-[10px] text-slate-500 mt-1">{event.event_type}</p>
    </div>
  );
};

export const CalendarPage = () => {
  const { api, user } = useAuth();
  const { subscribe } = useWebSocket();
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');
  
  const [content, setContent] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [channelFilter, setChannelFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(urlStatus || 'all');
  const [calendarTypeFilter, setCalendarTypeFilter] = useState('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState('all');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    content_type: 'Blog',
    calendar_type: 'other',
    channels: [],
    project_id: '',
    owner_id: '',
    draft_link: '',
    final_url: '',
    live_link: '',
    cta: '',
    target_audience: '',
    business_unit: '',
    status: 'idea',
    publish_date: '',
    publish_time: '',
    time_zone: 'UTC',
    approval_required: false
  });

  const fetchData = useCallback(async () => {
    try {
      const [contentRes, eventsRes, usersRes, projectsRes, configRes] = await Promise.all([
        api.get('/content'),
        api.get('/events'),
        api.get('/users'),
        api.get('/projects'),
        api.get('/content/config')
      ]);
      setContent(contentRes.data);
      setEvents(eventsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
      setBusinessUnits(configRes.data.business_units || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates for content and events
  useEffect(() => {
    const unsubscribes = [];
    
    // Content handlers
    unsubscribes.push(subscribe('content_created', (message) => {
      if (message.data) {
        setContent(prev => {
          if (prev.some(c => c.id === message.data.id)) return prev;
          return [...prev, message.data];
        });
      }
    }));
    
    unsubscribes.push(subscribe('content_updated', (message) => {
      if (message.data) {
        setContent(prev => prev.map(c => 
          c.id === message.data.id ? { ...c, ...message.data } : c
        ));
      }
    }));
    
    unsubscribes.push(subscribe('content_deleted', (message) => {
      if (message.data?.id) {
        setContent(prev => prev.filter(c => c.id !== message.data.id));
      }
    }));
    
    // Event handlers
    unsubscribes.push(subscribe('event_created', (message) => {
      if (message.data) {
        setEvents(prev => {
          if (prev.some(e => e.id === message.data.id)) return prev;
          return [...prev, message.data];
        });
      }
    }));
    
    unsubscribes.push(subscribe('event_updated', (message) => {
      if (message.data) {
        setEvents(prev => prev.map(e => 
          e.id === message.data.id ? { ...e, ...message.data } : e
        ));
      }
    }));
    
    unsubscribes.push(subscribe('event_deleted', (message) => {
      if (message.data?.id) {
        setEvents(prev => prev.filter(e => e.id !== message.data.id));
      }
    }));

    return () => unsubscribes.forEach(unsub => unsub());
  }, [subscribe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        owner_id: formData.owner_id || user.id,
        project_id: formData.project_id || null
      };
      
      let savedContent;
      if (editingContent) {
        const res = await api.put(`/content/${editingContent.id}`, payload);
        savedContent = res.data;
      } else {
        const res = await api.post('/content', payload);
        savedContent = res.data;
      }

      // Create linked task if requested
      if (createTask && !editingContent && taskData.name.trim()) {
        try {
          await api.post('/tasks', {
            name: taskData.name || `Task for: ${formData.title}`,
            description: `Auto-created task for content: ${formData.title}`,
            priority: taskData.priority,
            status: 'backlog',
            due_date: taskData.due_date || formData.publish_date || null,
            project_id: formData.project_id || null,
            content_id: savedContent?.id || null
          });
        } catch (taskErr) {
          console.error('Failed to create linked task:', taskErr);
        }
      }

      setDialogOpen(false);
      resetForm();
      setCreateTask(false);
      setTaskData({ name: '', priority: 'medium', due_date: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to save content:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    // If this content was auto-created from an event, open event detail instead
    if (item.event_id) {
      const linkedEvent = events.find(e => e.id === item.event_id);
      if (linkedEvent) {
        setSelectedEvent(linkedEvent);
        setEventDetailOpen(true);
        return;
      }
    }
    
    setEditingContent(item);
    setFormData({
      title: item.title,
      content_type: item.content_type,
      calendar_type: item.calendar_type || 'other',
      channels: item.channels || [],
      project_id: item.project_id || '',
      owner_id: item.owner_id,
      draft_link: item.draft_link || '',
      final_url: item.final_url || '',
      live_link: item.live_link || '',
      cta: item.cta || '',
      target_audience: item.target_audience || '',
      business_unit: item.business_unit || '',
      status: item.status,
      publish_date: item.publish_date || '',
      publish_time: item.publish_time || '',
      time_zone: item.time_zone || 'UTC',
      approval_required: item.approval_required
    });
    setDialogOpen(true);
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await api.delete(`/content/${contentId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete content:', error);
    }
  };

  const resetForm = () => {
    setEditingContent(null);
    setCreateTask(false);
    setTaskData({ name: '', priority: 'medium', due_date: '' });
    setFormData({
      title: '',
      content_type: 'Blog',
      calendar_type: 'other',
      channels: [],
      project_id: '',
      owner_id: '',
      draft_link: '',
      final_url: '',
      live_link: '',
      cta: '',
      target_audience: '',
      business_unit: '',
      status: 'idea',
      publish_date: '',
      publish_time: '',
      time_zone: 'UTC',
      approval_required: false
    });
  };

  // Drag and drop handlers for calendar
  const [dragOverDate, setDragOverDate] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);

  // Task creation fields for "also create a task" feature
  const [createTask, setCreateTask] = useState(false);
  const [taskData, setTaskData] = useState({
    name: '',
    priority: 'medium',
    due_date: ''
  });

  const handleDoubleClickDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    resetForm();
    setFormData(prev => ({ ...prev, publish_date: dateStr }));
    setDialogOpen(true);
  };

  const handleCalendarDrop = async (date, e) => {
    e.preventDefault();
    setDragOverDate(null);
    
    const contentId = e.dataTransfer.getData('contentId');
    const eventId = e.dataTransfer.getData('eventId');
    const itemType = e.dataTransfer.getData('contentType');
    const newDate = format(date, 'yyyy-MM-dd');
    
    try {
      if (itemType === 'content' && contentId) {
        // Update content publish_date
        await api.put(`/content/${contentId}`, { publish_date: newDate });
        fetchData();
      } else if (itemType === 'event' && eventId) {
        // Update event start_date
        await api.put(`/events/${eventId}`, { start_date: newDate });
        fetchData();
      }
    } catch (error) {
      console.error('Failed to update date:', error);
    }
  };

  const handleDragOver = (date, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(format(date, 'yyyy-MM-dd'));
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const toggleChannel = (channel) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  // Calendar helpers
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekStart = startOfWeek(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const filteredContent = content.filter(c => {
    const matchesChannel = channelFilter === 'all' || c.channels?.includes(channelFilter);
    const matchesType = typeFilter === 'all' || c.content_type === typeFilter;
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesCalendarType = calendarTypeFilter === 'all' || c.calendar_type === calendarTypeFilter;
    const matchesBusinessUnit = businessUnitFilter === 'all' || c.business_unit === businessUnitFilter;
    return matchesChannel && matchesType && matchesStatus && matchesCalendarType && matchesBusinessUnit;
  });

  const getContentForDay = (day) => {
    return filteredContent.filter(c => c.publish_date && isSameDay(parseISO(c.publish_date), day));
  };

  const getEventsForDay = (day) => {
    return events.filter(e => e.start_date && isSameDay(parseISO(e.start_date), day));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Content Calendar</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">{content.length} content items</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="new-content-btn">
              <Plus className="w-4 h-4" />
              New Content
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingContent ? 'Edit Content' : 'Create New Content'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Q1 Product Launch Blog Post"
                  required
                  data-testid="content-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Content Type</Label>
                  <Select value={formData.content_type} onValueChange={(v) => setFormData({...formData, content_type: v})}>
                    <SelectTrigger data-testid="content-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Calendar Type</Label>
                  <Select value={formData.calendar_type} onValueChange={(v) => setFormData({...formData, calendar_type: v})}>
                    <SelectTrigger data-testid="calendar-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CALENDAR_TYPE_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }}></span>
                            {config.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger data-testid="content-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Business Unit</Label>
                  <Select value={formData.business_unit || "none"} onValueChange={(v) => setFormData({...formData, business_unit: v === "none" ? "" : v})}>
                    <SelectTrigger data-testid="content-business-unit-select">
                      <SelectValue placeholder="Select business unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Business Unit</SelectItem>
                      {businessUnits.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Channels</Label>
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map(channel => (
                    <Badge
                      key={channel}
                      variant={formData.channels.includes(channel) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleChannel(channel)}
                    >
                      {channel}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({...formData, project_id: v === "none" ? "" : v})}>
                    <SelectTrigger data-testid="content-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Select value={formData.owner_id || "self"} onValueChange={(v) => setFormData({...formData, owner_id: v === "self" ? "" : v})}>
                    <SelectTrigger data-testid="content-owner-select">
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Self (Current User)</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Publish Date</Label>
                  <Input
                    type="date"
                    value={formData.publish_date}
                    onChange={(e) => setFormData({...formData, publish_date: e.target.value})}
                    data-testid="content-publish-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Publish Time</Label>
                  <Input
                    type="time"
                    value={formData.publish_time}
                    onChange={(e) => setFormData({...formData, publish_time: e.target.value})}
                    data-testid="content-publish-time"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Draft Link</Label>
                <Input
                  value={formData.draft_link}
                  onChange={(e) => setFormData({...formData, draft_link: e.target.value})}
                  placeholder="https://docs.google.com/..."
                  data-testid="content-draft-link"
                />
              </div>

              <div className="space-y-2">
                <Label>Live Link</Label>
                <Input
                  value={formData.live_link}
                  onChange={(e) => setFormData({...formData, live_link: e.target.value})}
                  placeholder="https://example.com/published-content"
                  data-testid="content-live-link"
                />
                <p className="text-xs text-slate-500">The public URL where this content is published</p>
              </div>
              
              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Input
                  value={formData.target_audience}
                  onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                  placeholder="Marketing professionals, CMOs"
                  data-testid="content-target-audience"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Approval Required</Label>
                <Switch
                  checked={formData.approval_required}
                  onCheckedChange={(v) => setFormData({...formData, approval_required: v})}
                  data-testid="content-approval-switch"
                />
              </div>

              {/* Also create a task */}
              {!editingContent && (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Also create a task</Label>
                    <Switch
                      checked={createTask}
                      onCheckedChange={(v) => {
                        setCreateTask(v);
                        if (v && !taskData.name) {
                          setTaskData(prev => ({ ...prev, name: `Task for: ${formData.title}`, due_date: formData.publish_date }));
                        }
                      }}
                      data-testid="create-task-switch"
                    />
                  </div>
                  {createTask && (
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                      <div className="space-y-1">
                        <Label className="text-xs">Task Name</Label>
                        <Input
                          value={taskData.name}
                          onChange={(e) => setTaskData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Task name"
                          data-testid="linked-task-name"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Priority</Label>
                          <Select value={taskData.priority} onValueChange={(v) => setTaskData(prev => ({ ...prev, priority: v }))}>
                            <SelectTrigger data-testid="linked-task-priority">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Due Date</Label>
                          <Input
                            type="date"
                            value={taskData.due_date}
                            onChange={(e) => setTaskData(prev => ({ ...prev, due_date: e.target.value }))}
                            data-testid="linked-task-due-date"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-content-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingContent ? 'Update' : 'Create'} Content
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar Controls */}
      <Card className="mcc-card">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Navigation row */}
            <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentDate(view === 'month' ? subMonths(currentDate, 1) : addDays(currentDate, -7))}
                  data-testid="calendar-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 min-w-[140px] sm:min-w-[160px] text-center">
                  {format(currentDate, view === 'month' ? 'MMMM yyyy' : "'Week of' MMM d")}
                </h2>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentDate(view === 'month' ? addMonths(currentDate, 1) : addDays(currentDate, 7))}
                  data-testid="calendar-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
                data-testid="calendar-today"
              >
                Today
              </Button>
            </div>
            
            {/* Filters row */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-end flex-wrap">
              <Select value={calendarTypeFilter} onValueChange={setCalendarTypeFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="filter-calendar-type">
                  <SelectValue placeholder="Calendar Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calendar Types</SelectItem>
                  {Object.entries(CALENDAR_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }}></span>
                        {config.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={businessUnitFilter} onValueChange={setBusinessUnitFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="filter-business-unit">
                  <SelectValue placeholder="Business Unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Business Units</SelectItem>
                  {businessUnits.map(unit => (
                    <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36" data-testid="filter-content-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-36" data-testid="filter-content-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {CONTENT_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Tabs value={view} onValueChange={setView} className="w-full sm:w-auto">
                <TabsList className="w-full sm:w-auto">
                  <TabsTrigger value="month" className="flex-1 sm:flex-none" data-testid="view-month">Month</TabsTrigger>
                  <TabsTrigger value="week" className="flex-1 sm:flex-none" data-testid="view-week">Week</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Color Legend */}
          <div className="flex flex-wrap gap-2 md:gap-4 mt-3 md:mt-4 pt-3 md:pt-4 border-t">
            {Object.entries(CALENDAR_TYPE_CONFIG).map(([key, config]) => (
              <div key={key} className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs text-slate-600">
                <span className="w-2 h-2 md:w-3 md:h-3 rounded shrink-0" style={{ backgroundColor: config.color }}></span>
                <span className="hidden sm:inline">{config.label}</span>
                <span className="sm:hidden">{config.label.split(' ')[0]}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      {view === 'month' && (
        <div className="calendar-grid" data-testid="calendar-month-view">
          {/* Header - Show abbreviated day names on mobile */}
          {[
            { full: 'Sunday', short: 'Sun', tiny: 'S' },
            { full: 'Monday', short: 'Mon', tiny: 'M' },
            { full: 'Tuesday', short: 'Tue', tiny: 'T' },
            { full: 'Wednesday', short: 'Wed', tiny: 'W' },
            { full: 'Thursday', short: 'Thu', tiny: 'T' },
            { full: 'Friday', short: 'Fri', tiny: 'F' },
            { full: 'Saturday', short: 'Sat', tiny: 'S' }
          ].map(day => (
            <div key={day.full} className="calendar-header-cell">
              <span className="hidden md:inline">{day.short}</span>
              <span className="md:hidden">{day.tiny}</span>
            </div>
          ))}
          
          {/* Days */}
          {calendarDays.map(day => {
            const dayContent = getContentForDay(day);
            const dayEvents = getEventsForDay(day);
            const totalItems = dayContent.length + dayEvents.length;
            const isCurrentMonth = isSameMonth(day, currentDate);
            const dayStr = format(day, 'yyyy-MM-dd');
            const isDragTarget = dragOverDate === dayStr;
            const isExpanded = expandedDay === dayStr;
            const MAX_VISIBLE = 4;
            const hasOverflow = totalItems > MAX_VISIBLE;
            
            return (
              <div
                key={day.toISOString()}
                className={`calendar-day-cell transition-all duration-150 ${isToday(day) ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''} ${
                  isDragTarget ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/50 scale-[1.02]' : ''
                }`}
                onDragOver={(e) => handleDragOver(day, e)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleCalendarDrop(day, e)}
                onDoubleClick={() => handleDoubleClickDay(day)}
                data-testid={`calendar-day-${dayStr}`}
              >
                <span className={`calendar-day-number ${isDragTarget ? 'text-blue-600' : ''}`}>{format(day, 'd')}</span>
                
                {/* Drop indicator when dragging over empty day */}
                {isDragTarget && dayContent.length === 0 && dayEvents.length === 0 && (
                  <div className="flex items-center justify-center h-8 border-2 border-dashed border-blue-300 rounded text-xs text-blue-500 mt-1">
                    Drop here
                  </div>
                )}
                
                <div className={`space-y-1 ${isExpanded ? 'max-h-[200px] overflow-y-auto scrollbar-thin' : 'overflow-hidden'}`}>
                  {(isExpanded ? dayContent : dayContent.slice(0, MAX_VISIBLE)).map(c => (
                    <CalendarEvent key={c.id} content={c} onClick={handleEdit} />
                  ))}
                  {(isExpanded ? dayEvents : dayEvents.slice(0, Math.max(0, MAX_VISIBLE - dayContent.length))).map(e => (
                    <DraggableEvent key={e.id} event={e} onClick={(evt) => { setSelectedEvent(evt); setEventDetailOpen(true); }} />
                  ))}
                </div>
                {hasOverflow && !isExpanded && (
                  <button
                    className="text-xs text-blue-500 hover:text-blue-700 pl-2 mt-0.5 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setExpandedDay(dayStr); }}
                    data-testid={`day-show-more-${dayStr}`}
                  >
                    +{totalItems - MAX_VISIBLE} more
                  </button>
                )}
                {isExpanded && (
                  <button
                    className="text-xs text-blue-500 hover:text-blue-700 pl-2 mt-0.5 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setExpandedDay(null); }}
                  >
                    Show less
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === 'week' && (
        <Card className="mcc-card overflow-x-auto" data-testid="calendar-week-view">
          <CardContent className="p-0 min-w-[600px]">
            <div className="grid grid-cols-7 border-b border-slate-200">
              {weekDays.map(day => (
                <div
                  key={day.toISOString()}
                  className={`p-4 text-center border-r border-slate-100 last:border-r-0 ${isToday(day) ? 'bg-blue-50' : ''}`}
                >
                  <p className="text-xs text-slate-500 uppercase">{format(day, 'EEE')}</p>
                  <p className={`text-lg font-semibold ${isToday(day) ? 'text-blue-600' : 'text-slate-900'}`}>
                    {format(day, 'd')}
                  </p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 min-h-[400px]">
              {weekDays.map(day => {
                const dayContent = getContentForDay(day);
                const dayEvents = getEventsForDay(day);
                const dayStr = format(day, 'yyyy-MM-dd');
                const isDragTarget = dragOverDate === dayStr;
                
                return (
                  <div
                    key={day.toISOString()}
                    className={`p-2 border-r border-slate-100 last:border-r-0 transition-all duration-150 ${
                      isToday(day) ? 'bg-blue-50/30' : ''
                    } ${isDragTarget ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/50' : ''}`}
                    onDragOver={(e) => handleDragOver(day, e)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleCalendarDrop(day, e)}
                    onDoubleClick={() => handleDoubleClickDay(day)}
                  >
                    {/* Drop indicator */}
                    {isDragTarget && dayContent.length === 0 && dayEvents.length === 0 && (
                      <div className="flex items-center justify-center h-16 border-2 border-dashed border-blue-300 rounded-lg text-sm text-blue-500 mb-2">
                        Drop here
                      </div>
                    )}
                    
                    <div className="space-y-2 max-h-[350px] overflow-y-auto scrollbar-thin">
                      {dayContent.map(c => (
                        <WeekContentCard key={c.id} content={c} onClick={handleEdit} />
                      ))}
                      {dayEvents.map(e => (
                        <WeekEventCard key={e.id} event={e} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content Type Legend */}
      <Card className="mcc-card">
        <CardContent className="p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Content Types</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(CONTENT_TYPE_COLORS).map(([type, colorClass]) => (
              <div key={type} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${colorClass}`} />
                <span className="text-sm text-slate-600">{type}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-100 border-l-2 border-emerald-500" />
              <span className="text-sm text-slate-600">Event</span>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">
            <span className="inline-flex items-center gap-1">
              <GripVertical className="w-3 h-3" />
              Drag items to reschedule &bull; Double-click a day to create new content
            </span>
          </p>
        </CardContent>
      </Card>

      {/* Event Detail Dialog (for event-linked content) */}
      {selectedEvent && (
        <Dialog open={eventDetailOpen} onOpenChange={(open) => { setEventDetailOpen(open); if (!open) setSelectedEvent(null); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="calendar-event-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-emerald-500" />
                Event Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900 text-base" data-testid="calendar-event-detail-name">{selectedEvent.name}</h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{selectedEvent.event_type}</Badge>
                <Badge className={
                  selectedEvent.status === 'live' ? 'bg-emerald-50 text-emerald-700' :
                  selectedEvent.status === 'completed' ? 'bg-purple-50 text-purple-700' :
                  selectedEvent.status === 'promotion_active' ? 'bg-blue-50 text-blue-700' :
                  'bg-slate-100 text-slate-700'
                }>
                  {selectedEvent.status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Start Date</p>
                  <p className="text-slate-800 font-medium">{selectedEvent.start_date ? format(parseISO(selectedEvent.start_date), 'MMM d, yyyy') : 'N/A'}</p>
                </div>
                {selectedEvent.end_date && (
                  <div>
                    <p className="text-slate-500 text-xs">End Date</p>
                    <p className="text-slate-800 font-medium">{format(parseISO(selectedEvent.end_date), 'MMM d, yyyy')}</p>
                  </div>
                )}
                {selectedEvent.location && (
                  <div>
                    <p className="text-slate-500 text-xs">Location</p>
                    <p className="text-slate-800">{selectedEvent.location}</p>
                  </div>
                )}
                <div>
                  <p className="text-slate-500 text-xs">Owner</p>
                  <p className="text-slate-800">{users.find(u => u.id === selectedEvent.owner_id)?.name || 'Unknown'}</p>
                </div>
              </div>
              {selectedEvent.registration_link && (
                <div>
                  <p className="text-slate-500 text-xs mb-1">Registration Link</p>
                  <a href={selectedEvent.registration_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline break-all">
                    {selectedEvent.registration_link}
                  </a>
                </div>
              )}
              {selectedEvent.checklist?.length > 0 && (
                <div>
                  <p className="text-slate-500 text-xs mb-2">Checklist ({selectedEvent.checklist.filter(c => c.completed).length}/{selectedEvent.checklist.length})</p>
                  <div className="space-y-1">
                    {selectedEvent.checklist.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${item.completed ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                          {item.completed ? '✓' : ''}
                        </span>
                        <span className={item.completed ? 'line-through text-slate-400' : 'text-slate-700'}>{item.item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => { setEventDetailOpen(false); setSelectedEvent(null); window.location.href = '/events'; }} data-testid="go-to-events-btn">
                  Open in Events
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
